import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Layout } from './components/ui/Layout';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { PublicLandingPage } from './pages/PublicLandingPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { ShippingPolicyPage } from './pages/ShippingPolicyPage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';
import { ContactUsPage } from './pages/ContactUsPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProfilePage } from './pages/ProfilePage';
import { SearchPage } from './pages/SearchPage';
import { SearchReportPage } from './pages/SearchReportPage';
import { MyReportsPage } from './pages/MyReportsPage';
import { SubscriptionPlansPage } from './pages/SubscriptionPlansPage';
import { PaymentHistoryPage } from './pages/PaymentHistoryPage';
import { GoogleCallbackPage } from './pages/GoogleCallbackPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import AdminOrganizationsPage from './pages/AdminOrganizationsPage';
import AdminApiManagementPage from './pages/AdminApiManagementPage';
import { AdminAIProvidersPage } from './pages/AdminAIProvidersPage';
import { AdminAISummaryConfigPage } from './pages/AdminAISummaryConfigPage';
import { AdminApiContentTransformerPage } from './pages/AdminApiContentTransformerPage';
import { AdminMonitoringPage } from './pages/AdminMonitoringPage';
import { AdminSubscriptionPlansPage } from './pages/AdminSubscriptionPlansPage';
import AdminAllSearchesReportPage from './pages/AdminAllSearchesReportPage';
import { AdminPaymentsPage } from './pages/AdminPaymentsPage';
import { AdminOfflinePaymentsPage } from './pages/AdminOfflinePaymentsPage';
import AdminSearchParametersPage from './pages/AdminSearchParametersPage';
// Files page removed
import { useAuth } from './contexts/AuthContext';
import './App.css';
import { AdminIntCoinsReportPage } from './pages/AdminIntCoinsReportPage';
import { AdminAPIResponsesPage } from './pages/AdminAPIResponsesPage';
import { AdminAPILogsPage } from './pages/AdminAPILogsPage';
import { AdminOsintTestPage } from './pages/AdminOsintTestPage';
import AdminUniversalOSINTPage from './pages/AdminUniversalOSINTPage';
import { AdminLawEnforcementPage } from './pages/AdminLawEnforcementPage';
import { AdminReportTemplatesPage } from './pages/AdminReportTemplatesPage';
import { APIKeyManagementPage } from './pages/APIKeyManagementPage';
import { ReferralPage } from './pages/ReferralPage';
import SessionManager from './components/admin/SessionManager';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  if (!user.isStaff && !user.isSuperuser) {
    return <Navigate to="/app/dashboard" />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <div className="App">
              <Routes>
              <Route path="/" element={<PublicLandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/home/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/home/terms" element={<TermsOfServicePage />} />
              <Route path="/home/shipping" element={<ShippingPolicyPage />} />
              <Route path="/home/refund" element={<RefundPolicyPage />} />
              <Route path="/home/contact_us" element={<ContactUsPage />} />
              <Route path="/google-callback" element={<GoogleCallbackPage />} />
              
              <Route path="/app" element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }>
                <Route index element={<Navigate to="/app/dashboard" />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="search/:searchId/report" element={<SearchReportPage />} />
                <Route path="reports" element={<MyReportsPage />} />
                <Route path="subscriptions" element={<SubscriptionPlansPage />} />
                <Route path="payments" element={<PaymentHistoryPage />} />
                {/* Files route removed */}
                <Route path="api-keys" element={<APIKeyManagementPage />} />
                <Route path="referrals" element={<ReferralPage />} />
                
                {/* Admin Routes */}
                <Route path="admin/users" element={
                  <AdminRoute>
                    <AdminUsersPage />
                  </AdminRoute>
                } />
                <Route path="admin/organizations" element={
                  <AdminRoute>
                    <AdminOrganizationsPage />
                  </AdminRoute>
                } />
                <Route path="admin/api-management" element={
                  <AdminRoute>
                    <AdminApiManagementPage />
                  </AdminRoute>
                } />
                <Route path="admin/ai-providers" element={
                  <AdminRoute>
                    <AdminAIProvidersPage />
                  </AdminRoute>
                } />
                <Route path="admin/ai-summary-configs" element={
                  <AdminRoute>
                    <AdminAISummaryConfigPage />
                  </AdminRoute>
                } />
                <Route path="admin/api-content-transformer" element={
                  <AdminRoute>
                    <AdminApiContentTransformerPage />
                  </AdminRoute>
                } />
                <Route path="admin/api-responses" element={
                  <AdminRoute>
                    <AdminAPIResponsesPage />
                  </AdminRoute>
                } />
                <Route path="admin/api-logs" element={
                  <AdminRoute>
                    <AdminAPILogsPage />
                  </AdminRoute>
                } />
                <Route path="admin/report-templates" element={
                  <AdminRoute>
                    <AdminReportTemplatesPage />
                  </AdminRoute>
                } />
                <Route path="admin/all-searches" element={
                  <AdminRoute>
                    <AdminAllSearchesReportPage />
                  </AdminRoute>
                } />
                <Route path="admin/session-manager" element={
                  <AdminRoute>
                    <SessionManager />
                  </AdminRoute>
                } />
                <Route path="admin/monitoring" element={
                  <AdminRoute>
                    <AdminMonitoringPage />
                  </AdminRoute>
                } />
                <Route path="admin/subscription-plans" element={
                  <AdminRoute>
                    <AdminSubscriptionPlansPage />
                  </AdminRoute>
                } />
                <Route path="admin/payments" element={
                  <AdminRoute>
                    <AdminPaymentsPage />
                  </AdminRoute>
                } />
                <Route path="admin/offline-payments" element={
                  <AdminRoute>
                    <AdminOfflinePaymentsPage />
                  </AdminRoute>
                } />
                <Route path="admin/intcoins-report" element={
                  <AdminRoute>
                    <AdminIntCoinsReportPage />
                  </AdminRoute>
                } />
                <Route path="admin/osint-test" element={
                  <AdminRoute>
                    <AdminOsintTestPage />
                  </AdminRoute>
                } />
                <Route path="admin/universal-osint" element={
                  <AdminRoute>
                    <AdminUniversalOSINTPage />
                  </AdminRoute>
                } />
                <Route path="admin/search-parameters" element={
                  <AdminRoute>
                    <AdminSearchParametersPage />
                  </AdminRoute>
                } />
                <Route path="admin/law-enforcement" element={
                  <AdminRoute>
                    <AdminLawEnforcementPage />
                  </AdminRoute>
                } />
              </Route>
            </Routes>
            </div>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
